import { useParams } from "react-router-dom";
import {
  Box,
  VStack,
  Heading,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Divider,
  Spinner,
} from "@chakra-ui/react";

import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase"; // gastro-abst に接続された db

import DicomLinkButton from "../components/Dicomlinkbutton";
import BackButton from "../components/Backbutton";

type CaseType = {
  id: string;
  casetitle: string;
  detail: string;
  members: string;
  reader: string;
  sessionid: string;
  dicomlink?: string[];
};

export default function CaseInformation() {
  const { year, caseid } = useParams();

  const [chosenCase, setChosenCase] = useState<CaseType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCase = async () => {
      if (!caseid) return;

      try {
        const docRef = doc(db, "cases", caseid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setChosenCase({
            id: docSnap.id,
            ...(docSnap.data() as Omit<CaseType, "id">),
          });
        } else {
          console.warn("指定された症例データ(cases/" + caseid + ")が存在しません");
        }
      } catch (error) {
        console.error("Firestore取得エラー:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCase();
  }, [caseid]);

  useEffect(() => {
    document.title = chosenCase?.casetitle || "症例情報";
  }, [chosenCase]);

  const grayShades = [
    "gray.50",
    "gray.100",
    "gray.200",
    "gray.300",
    "gray.400",
  ];

  const dividerColors = [...grayShades].reverse();
  const [bgIndex, setBgIndex] = useState(0);

  if (loading) {
    return (
      <Box minH="100vh" display="flex" justifyContent="center" alignItems="center">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  if (!chosenCase) {
    return (
      <Box minH="100vh" p={8}>
        <Heading size="md" mb={4}>症例が見つかりませんでした</Heading>
        <BackButton />
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      bg={grayShades[bgIndex]}
      transition="background 0.3s ease"
      py={8}
    >
      <Box maxW="800px" mx="auto" px={4}>
        <VStack align="stretch" spacing={6}>
          {/* 背景調整 */}
          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={2}>
              室内の明るさに合わせてページ背景の明るさを調整してください
            </Text>

            <Slider
              defaultValue={0}
              min={0}
              max={4}
              step={1}
              onChange={(val) => setBgIndex(val)}
              mb={4}
            >
              <SliderTrack bg="gray.200">
                <SliderFilledTrack bg="gray.500" />
              </SliderTrack>
              <SliderThumb />
            </Slider>

            <BackButton />
          </Box>

          <Divider borderColor={dividerColors[bgIndex]} />

          {/* 症例情報 */}
          <Box>
            <Heading size="lg" mb={2}>
              {chosenCase.casetitle}
            </Heading>

            {/* 安全のため普通の子要素として出力（whiteSpace="pre-line"で改行は有効） */}
            <Text
              fontSize="md"
              color="gray.600"
              whiteSpace="pre-line"
              mb={4}
            >
              {chosenCase.members}
            </Text>

            <Divider mb={4} borderColor={dividerColors[bgIndex]} />

            <Text whiteSpace="pre-line" fontSize="md">
              {chosenCase.detail}
            </Text>
          </Box>

          {/* DICOMリンク */}
          {chosenCase.dicomlink && chosenCase.dicomlink.length > 0 && (
            <>
              <Divider borderColor={dividerColors[bgIndex]} />

              <Box>
                {chosenCase.dicomlink.map((link, index) => (
                  <Box key={link} mb={2}>
                    <DicomLinkButton
                      link={link}
                      label={`Dicom画像 ${index + 1}`}
                    />
                  </Box>
                ))}
              </Box>
            </>
          )}

          <Divider borderColor={dividerColors[bgIndex]} />

          {/* 戻る */}
          <Box>
            <BackButton />
          </Box>
        </VStack>
      </Box>
    </Box>
  );
}